import React from "react";
import { useQueryClient } from "react-query";
import { apiDirectCall, useApiCache } from "customHooks/useApi";
import {
    getApiCacheQueryKey,
    getWhatsappConversationQueryScope,
    getWhatsappPreRegistrationAccessQueryScope
} from "customHooks/useApi/authCacheIsolation";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import {
    buildWhatsappWebsocketUrl,
    canAccessApplicantWhatsappBaseConversation
} from "./helpers";

type WhatsappLiveStatus =
    | "idle"
    | "connecting"
    | "connected"
    | "reconnecting"
    | "disconnected";

interface IUseWhatsappConversationParams {
    applicantId: string;
    companyId: number;
    enabled: boolean;
    principal: string | null;
    canShowApplicants: boolean;
    hasJobAccess: boolean;
    whatsappPreRegistrationEnabled: boolean;
}

const RECONNECT_DELAYS_MS = [1500, 3000, 5000, 8000];
const PRE_REGISTRATION_ACCESS_REVISION_ENDPOINT =
    "/whatsapp/preregistrationaccessrevision";
const PRE_REGISTRATION_ACCESS_REVISION_INTERVAL_MS = 2000;
const MAX_ACCESS_LEASE_DURATION_MS = 10000;
const ACCESS_FINGERPRINT_PATTERN = /^v1:[a-f0-9]{64}$/;

const getRealtimeTicket = async (params: {
    applicantId: string;
    companyId: number;
}) => {
    const result = await apiDirectCall("/whatsapp/realtimeticket", "post", {
        applicantId: params.applicantId,
        companyId: params.companyId
    });

    return getWhatsappApiResponseData(result).ticket || "";
};

const getReconnectDelay = (attempt: number) => {
    return RECONNECT_DELAYS_MS[
        Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
    ];
};

const isTerminalRealtimeFailure = (error: any) => {
    const statusCode = Number(error?.response?.status || 0);
    return statusCode >= 400 && statusCode < 500 && statusCode !== 429;
};

export const buildWhatsappConversationRequestPayload = (params: {
    applicantId: string;
    companyId: number;
}) => ({
    applicantId: params.applicantId,
    companyId: params.companyId,
    limit: 100,
    contactEpisodeCapability: "CONTACT_EPISODE_V1"
});

export const useWhatsappConversation = (
    params: IUseWhatsappConversationParams
) => {
    const {
        applicantId,
        companyId,
        enabled,
        principal,
        canShowApplicants,
        hasJobAccess,
        whatsappPreRegistrationEnabled
    } = params;
    const queryClient = useQueryClient();
    const reconnectAttemptsRef = React.useRef(0);
    const reconnectTimeoutRef = React.useRef<number | null>(null);
    const activeSocketRef = React.useRef<WebSocket | null>(null);

    const requestPayload = React.useMemo(
        () =>
            buildWhatsappConversationRequestPayload({ applicantId, companyId }),
        [applicantId, companyId]
    );
    const accessRevisionPayload = React.useMemo(
        () => ({ companyId }),
        [companyId]
    );
    const accessRevisionQueryEnabled =
        enabled && canShowApplicants && Boolean(principal);
    const accessRevisionQueryScope = React.useMemo(
        () =>
            getWhatsappPreRegistrationAccessQueryScope({
                principal,
                companyId,
                canShowApplicants,
                whatsappEnabled: enabled
            }),
        [canShowApplicants, companyId, enabled, principal]
    );
    const [
        accessRevisionState,
        fetchAccessRevision,
        ,
        isAccessRevisionFetching
    ] = useApiCache(
        PRE_REGISTRATION_ACCESS_REVISION_ENDPOINT,
        "post",
        accessRevisionPayload,
        true,
        0,
        false,
        PRE_REGISTRATION_ACCESS_REVISION_ENDPOINT,
        {
            queryScope: accessRevisionQueryScope,
            abortOnUnmount: true,
            cacheTime: 0,
            enabled: accessRevisionQueryEnabled,
            retry: false,
            refetchInterval: PRE_REGISTRATION_ACCESS_REVISION_INTERVAL_MS,
            refetchIntervalInBackground: true,
            refetchOnWindowFocus: true,
            syncQueryArguments: true
        }
    );
    const fetchAccessRevisionRef = React.useRef(fetchAccessRevision);
    React.useEffect(() => {
        fetchAccessRevisionRef.current = fetchAccessRevision;
    }, [fetchAccessRevision]);
    React.useEffect(() => {
        if (accessRevisionQueryEnabled) {
            fetchAccessRevisionRef.current(accessRevisionPayload);
        }
    }, [
        accessRevisionPayload,
        accessRevisionQueryEnabled,
        accessRevisionQueryScope
    ]);
    const expectedAccessRevisionQueryKey = React.useMemo(
        () =>
            getApiCacheQueryKey({
                mainKey: PRE_REGISTRATION_ACCESS_REVISION_ENDPOINT,
                address: PRE_REGISTRATION_ACCESS_REVISION_ENDPOINT,
                verb: "post",
                params: accessRevisionPayload,
                queryScope: accessRevisionQueryScope
            }),
        [accessRevisionPayload, accessRevisionQueryScope]
    );
    const isAccessRevisionScopeCurrent =
        JSON.stringify(accessRevisionState.useQueryKey) ===
        JSON.stringify(expectedAccessRevisionQueryKey);
    const accessRevisionData = getWhatsappApiResponseData(
        accessRevisionState.response
    ) as Compleo.IObject;
    const candidateAccessFingerprint = String(
        accessRevisionData.accessFingerprint || ""
    );
    const accessLease = (accessRevisionData.accessLease ||
        {}) as Compleo.IObject;
    const accessLeaseId = String(accessLease.id || "");
    const accessLeaseDurationMs = Number(accessLease.durationMs || 0);
    const isAccessLeaseEnvelopeValid =
        Boolean(accessLeaseId) &&
        Number.isSafeInteger(accessLeaseDurationMs) &&
        accessLeaseDurationMs > 0 &&
        accessLeaseDurationMs <= MAX_ACCESS_LEASE_DURATION_MS;
    const [accessLeaseExpiresAtMs, setAccessLeaseExpiresAtMs] =
        React.useState(0);
    React.useEffect(() => {
        if (
            !accessRevisionQueryEnabled ||
            !isAccessRevisionScopeCurrent ||
            accessRevisionState.status !== "success" ||
            !ACCESS_FINGERPRINT_PATTERN.test(candidateAccessFingerprint) ||
            !isAccessLeaseEnvelopeValid
        ) {
            setAccessLeaseExpiresAtMs(0);
            return;
        }

        const expiresAtMs = Date.now() + accessLeaseDurationMs;
        setAccessLeaseExpiresAtMs(expiresAtMs);
        const expirationTimer = window.setTimeout(() => {
            setAccessLeaseExpiresAtMs(0);
        }, accessLeaseDurationMs);

        return () => window.clearTimeout(expirationTimer);
    }, [
        accessLeaseDurationMs,
        accessLeaseId,
        accessRevisionQueryEnabled,
        accessRevisionState.status,
        candidateAccessFingerprint,
        isAccessLeaseEnvelopeValid,
        isAccessRevisionScopeCurrent
    ]);
    const accessFingerprint =
        accessRevisionQueryEnabled &&
        isAccessRevisionScopeCurrent &&
        accessRevisionState.status === "success" &&
        ACCESS_FINGERPRINT_PATTERN.test(candidateAccessFingerprint) &&
        isAccessLeaseEnvelopeValid &&
        Date.now() < accessLeaseExpiresAtMs
            ? candidateAccessFingerprint
            : "";
    const conversationEnabled = enabled && Boolean(accessFingerprint);
    const conversationQueryScope = React.useMemo(
        () =>
            getWhatsappConversationQueryScope({
                principal,
                companyId,
                canShowApplicants,
                hasJobAccess,
                whatsappEnabled: enabled,
                whatsappPreRegistrationEnabled,
                accessFingerprint
            }),
        [
            accessFingerprint,
            canShowApplicants,
            companyId,
            enabled,
            hasJobAccess,
            principal,
            whatsappPreRegistrationEnabled
        ]
    );

    const [
        conversationState,
        fetchConversation,
        invalidateConversationQuery,
        isConversationFetching
    ] = useApiCache(
        "/whatsapp/listmessages",
        "post",
        requestPayload,
        true,
        1000 * 30,
        false,
        "/whatsapp/listmessages",
        {
            queryScope: conversationQueryScope,
            abortOnUnmount: true,
            cacheTime: 0,
            enabled: conversationEnabled,
            syncQueryArguments: true
        }
    );
    const expectedConversationQueryKey = React.useMemo(
        () =>
            getApiCacheQueryKey({
                mainKey: "/whatsapp/listmessages",
                address: "/whatsapp/listmessages",
                verb: "post",
                params: requestPayload,
                queryScope: conversationQueryScope
            }),
        [conversationQueryScope, requestPayload]
    );
    const isConversationScopeCurrent =
        conversationEnabled &&
        JSON.stringify(conversationState.useQueryKey) ===
            JSON.stringify(expectedConversationQueryKey);
    const safeConversationState = isConversationScopeCurrent
        ? conversationState
        : {
              ...conversationState,
              status:
                  accessRevisionState.status === "error" ? "error" : "initial",
              exception:
                  accessRevisionState.status === "error"
                      ? accessRevisionState.exception
                      : null,
              response: {}
          };
    const fetchConversationRef = React.useRef(fetchConversation);
    const invalidateConversationQueryRef = React.useRef(
        invalidateConversationQuery
    );
    const conversationQueryKeyRef = React.useRef(conversationState.useQueryKey);
    const conversationData = getWhatsappApiResponseData(
        safeConversationState.response
    ) as Compleo.IObject;
    const canAccessBaseConversation =
        canAccessApplicantWhatsappBaseConversation(conversationData);

    React.useEffect(() => {
        fetchConversationRef.current = fetchConversation;
    }, [fetchConversation]);

    React.useEffect(() => {
        invalidateConversationQueryRef.current = invalidateConversationQuery;
    }, [invalidateConversationQuery]);

    React.useEffect(() => {
        conversationQueryKeyRef.current = conversationState.useQueryKey;
    }, [conversationState.useQueryKey]);

    const [liveStatus, setLiveStatus] =
        React.useState<WhatsappLiveStatus>("idle");

    const clearReconnectTimeout = React.useCallback(() => {
        if (reconnectTimeoutRef.current !== null) {
            window.clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, []);

    const refreshConversation = React.useCallback(() => {
        if (!conversationEnabled) {
            return;
        }

        if (conversationQueryKeyRef.current) {
            queryClient.invalidateQueries(conversationQueryKeyRef.current);
            return;
        }

        if (invalidateConversationQueryRef.current) {
            invalidateConversationQueryRef.current(requestPayload);
            return;
        }

        fetchConversationRef.current(requestPayload);
    }, [conversationEnabled, queryClient, requestPayload]);

    React.useEffect(() => {
        if (conversationEnabled) {
            fetchConversationRef.current(requestPayload);
            return;
        }

        setLiveStatus("idle");
    }, [conversationEnabled, conversationQueryScope, requestPayload]);

    React.useEffect(() => {
        if (
            !conversationEnabled ||
            safeConversationState.status !== "success" ||
            !canAccessBaseConversation ||
            typeof WebSocket === "undefined"
        ) {
            setLiveStatus("idle");
            return;
        }

        let disposed = false;
        let currentSocket: WebSocket | null = null;
        activeSocketRef.current = null;

        const disconnectSocket = () => {
            if (currentSocket) {
                const socketToClose = currentSocket;
                currentSocket = null;
                activeSocketRef.current = null;
                socketToClose.close();
            }
        };

        const scheduleReconnect = () => {
            clearReconnectTimeout();

            const currentDelay = getReconnectDelay(
                reconnectAttemptsRef.current
            );
            reconnectAttemptsRef.current += 1;

            reconnectTimeoutRef.current = window.setTimeout(() => {
                if (
                    !disposed &&
                    conversationEnabled &&
                    canAccessBaseConversation
                ) {
                    void connectSocket();
                }
            }, currentDelay);
        };

        const connectSocket = async () => {
            if (
                disposed ||
                !conversationEnabled ||
                !canAccessBaseConversation
            ) {
                return;
            }

            disconnectSocket();
            clearReconnectTimeout();
            setLiveStatus(
                reconnectAttemptsRef.current > 0 ? "reconnecting" : "connecting"
            );

            try {
                const ticket = await getRealtimeTicket({
                    applicantId,
                    companyId
                });

                if (!ticket || disposed || !conversationEnabled) {
                    return;
                }

                const url = buildWhatsappWebsocketUrl(
                    import.meta.env.VITE_API_ADDRESS,
                    {
                        ticket,
                        companyId,
                        applicantId
                    }
                );

                const websocket = new WebSocket(url);
                currentSocket = websocket;
                activeSocketRef.current = websocket;

                websocket.onopen = () => {
                    if (disposed || activeSocketRef.current !== websocket) {
                        return;
                    }

                    reconnectAttemptsRef.current = 0;
                    setLiveStatus("connected");
                };

                websocket.onmessage = () => {
                    if (disposed || activeSocketRef.current !== websocket) {
                        return;
                    }

                    refreshConversation();
                };

                websocket.onclose = (event) => {
                    if (
                        disposed ||
                        !conversationEnabled ||
                        activeSocketRef.current !== websocket
                    ) {
                        return;
                    }

                    activeSocketRef.current = null;
                    setLiveStatus("disconnected");
                    if (event.code === 4000 || event.code === 4001) {
                        return;
                    }

                    scheduleReconnect();
                };

                websocket.onerror = () => {
                    if (activeSocketRef.current === websocket) {
                        websocket.close();
                    }
                };
            } catch (error) {
                if (disposed || !conversationEnabled) {
                    return;
                }

                setLiveStatus("disconnected");
                if (isTerminalRealtimeFailure(error)) {
                    return;
                }

                scheduleReconnect();
            }
        };

        void connectSocket();

        return () => {
            disposed = true;
            clearReconnectTimeout();
            disconnectSocket();
        };
    }, [
        applicantId,
        canAccessBaseConversation,
        clearReconnectTimeout,
        companyId,
        conversationEnabled,
        refreshConversation,
        safeConversationState.status
    ]);

    return {
        conversationState: safeConversationState,
        conversationData,
        isConversationFetching:
            isConversationFetching === true ||
            (!accessFingerprint && isAccessRevisionFetching === true),
        liveStatus,
        refreshConversation
    };
};

export type UseWhatsappConversationReturn = ReturnType<
    typeof useWhatsappConversation
>;
